import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronRight, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface WorkflowStep {
  step_number: number;
  approver_email: string;
  required: boolean;
}

interface Workflow {
  id: string;
  name: string;
  is_active: boolean;
  steps: WorkflowStep[];
  created_at: string;
  chain_mode?: boolean;
}

export const ApprovalWorkflowManager = () => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [workflowName, setWorkflowName] = useState("");
  const [chainMode, setChainMode] = useState(false);
  const [steps, setSteps] = useState<WorkflowStep[]>([{ step_number: 1, approver_email: "", required: true }]);
  const { toast } = useToast();

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('approval_workflows')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWorkflows((data || []).map(w => ({
        ...w,
        steps: (w.steps as unknown) as WorkflowStep[]
      })));
    } catch (error) {
      console.error("Error fetching workflows:", error);
      toast({
        title: "Error",
        description: "Failed to load approval workflows",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = () => {
    setSteps([...steps, { step_number: steps.length + 1, approver_email: "", required: true }]);
  };

  const handleRemoveStep = (index: number) => {
    if (steps.length > 1) {
      const newSteps = steps.filter((_, i) => i !== index);
      setSteps(newSteps.map((step, i) => ({ ...step, step_number: i + 1 })));
    }
  };

  const handleStepChange = (index: number, field: keyof WorkflowStep, value: string | boolean) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleSaveWorkflow = async () => {
    if (!workflowName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a workflow name",
        variant: "destructive",
      });
      return;
    }

    if (!chainMode) {
      const invalidSteps = steps.filter(step => !step.approver_email.trim());
      if (invalidSteps.length > 0) {
        toast({
          title: "Validation Error",
          description: "All steps must have an approver email",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const workflowData = {
        user_id: user.id,
        name: workflowName,
        steps: chainMode ? [] as any : steps as any,
        chain_mode: chainMode,
        is_active: true,
      };

      if (editingWorkflow) {
        const { error } = await supabase
          .from('approval_workflows')
          .update(workflowData)
          .eq('id', editingWorkflow.id);

        if (error) throw error;

        toast({
          title: "Workflow updated",
          description: "Approval workflow has been updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('approval_workflows')
          .insert(workflowData);

        if (error) throw error;

        toast({
          title: "Workflow created",
          description: "Approval workflow has been created successfully",
        });
      }

      setDialogOpen(false);
      resetForm();
      fetchWorkflows();
    } catch (error) {
      console.error("Error saving workflow:", error);
      toast({
        title: "Error",
        description: "Failed to save workflow",
        variant: "destructive",
      });
    }
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    setEditingWorkflow(workflow);
    setWorkflowName(workflow.name);
    setChainMode((workflow as any).chain_mode ?? false);
    setSteps(workflow.steps.length > 0 ? workflow.steps : [{ step_number: 1, approver_email: "", required: true }]);
    setDialogOpen(true);
  };

  const handleDeleteWorkflow = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_workflows')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Workflow deleted",
        description: "Approval workflow has been deleted",
      });

      fetchWorkflows();
    } catch (error) {
      console.error("Error deleting workflow:", error);
      toast({
        title: "Error",
        description: "Failed to delete workflow",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setEditingWorkflow(null);
    setWorkflowName("");
    setChainMode(false);
    setSteps([{ step_number: 1, approver_email: "", required: true }]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingWorkflow ? 'Edit' : 'Create'} Approval Workflow</DialogTitle>
              <DialogDescription>
                Define a multi-step approval process with specific approvers
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="workflow-name">Workflow Name</Label>
                <Input
                  id="workflow-name"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="e.g., Marketing Campaign Approval"
                />
              </div>
              
              {/* Chain mode toggle */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                <input
                  type="checkbox"
                  id="chain-mode"
                  checked={chainMode}
                  onChange={e => setChainMode(e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-[#0D2240]"
                />
                <div>
                  <Label htmlFor="chain-mode" className="font-medium cursor-pointer">Organogram Chain Mode</Label>
                  <p className="text-xs text-muted-foreground">Approvers are automatically resolved by walking up the organogram from the submitter's unit. No manual steps required.</p>
                </div>
              </div>

              {!chainMode && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Approval Steps</Label>
                  <Button size="sm" variant="outline" onClick={handleAddStep}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Step
                  </Button>
                </div>

                {steps.map((step, index) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
                          {step.step_number}
                        </div>
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label htmlFor={`approver-${index}`}>Approver Email</Label>
                            <Input
                              id={`approver-${index}`}
                              type="email"
                              value={step.approver_email}
                              onChange={(e) => handleStepChange(index, 'approver_email', e.target.value)}
                              placeholder="approver@company.com"
                            />
                          </div>
                        </div>
                        {steps.length > 1 && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemoveStep(index)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              )} {/* end !chainMode */}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveWorkflow}>
                  {editingWorkflow ? 'Update' : 'Create'} Workflow
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading workflows...
          </CardContent>
        </Card>
      ) : workflows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No workflows yet</h3>
            <p className="text-justify text-muted-foreground mb-4">
              Create your first approval workflow to start managing content approvals
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {workflows.map((workflow) => (
            <Card key={workflow.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {workflow.name}
                      <Badge variant={workflow.is_active ? "default" : "secondary"}>
                        {workflow.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {(workflow as any).chain_mode
                        ? 'Organogram chain mode'
                        : `${workflow.steps.length} step${workflow.steps.length !== 1 ? 's' : ''}`} •{' '}
                      Created {new Date(workflow.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEditWorkflow(workflow)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.confirm(`Delete "${workflow.name}"? Posts already awaiting approval in this workflow will be affected. This cannot be undone.`) && handleDeleteWorkflow(workflow.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 overflow-x-auto">
                  {(workflow as any).chain_mode ? (
                    <div className="bg-sidebar p-3 rounded-lg border flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#0D2240]" />
                      <span className="text-sm font-medium text-[#0D2240]">Organogram chain — approvers resolved dynamically</span>
                    </div>
                  ) : workflow.steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-2 flex-shrink-0">
                      <div className="bg-sidebar p-3 rounded-lg border">
                        <div className="text-xs text-muted-foreground mb-1">Step {step.step_number}</div>
                        <div className="text-sm font-medium">{step.approver_email}</div>
                      </div>
                      {index < workflow.steps.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>

            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
